import {
  AS400_EXPORT_CARRIED_WITH,
  as400ColumnEditKey,
  foldToAs400Ascii,
  isPriceNumberFieldCode,
  nonAs400AsciiCharacters,
} from '@erp/rpc';
import { applyPriceColumnNumber } from '@erp/shared-utils';
import type {
  As400ExportColumn,
  As400ExportRow,
  As400ExportRowSource,
} from '@erp/rpc';
import { correctedSlashCount } from '@erp/item-master-rules';
import type { StagedView } from './BeforeAfterToggle';
import {
  asCellText,
  persistedCellValue,
  revertCellValue,
} from './as400ExportParts';
import type { ItemAddLookup } from './itemAddPresence';
import type { StockUomLookup } from './itemStockUom';

/** One export row's unsaved cell edits, kept until saved or discarded. */
export interface PendingRowEdit {
  /** The edited row's stable key — how a save routes the edit (an authored
   * new row saves through the new-row path; everything else stages to its
   * ingest sources). */
  rowKey: string;
  /** The ingest source row(s) behind the export row (captured at edit time).
   * Empty for an authored new row, which has none. */
  sources: As400ExportRowSource[];
  /** Field code → typed value (null = cleared cell). */
  values: Record<string, string | null>;
  /** Field code → live ingest value when the field was first edited. */
  originalValues: Record<string, string | null>;
}

/** Unsaved edits per export row, keyed by the row's stable `key`. */
export type PendingEditsByRowKey = Map<string, PendingRowEdit>;

/**
 * Record one typed cell into the pending-edit map (immutably). Typing back the
 * currently persisted value (staged if present, else live) removes the field —
 * it is no longer a pending change. Any other value is pending, including a
 * saved cell's revert value ({@link revertCellValue}): Save sends it, and the
 * server drops the staged edit it reverts.
 */
export function applyCellEdit(
  pending: PendingEditsByRowKey,
  row: As400ExportRow,
  code: string,
  typed: string | null,
): PendingEditsByRowKey {
  const next = new Map(pending);
  const existing = next.get(row.key);
  const values = { ...existing?.values };
  const originalValues = { ...existing?.originalValues };

  if (typed === persistedCellValue(row, code)) {
    delete values[code];
    delete originalValues[code];
  } else {
    values[code] = typed;
    // The drift baseline is the live ingest value, not the staged one.
    if (!(code in originalValues)) {
      originalValues[code] = row.values[code] ?? null;
    }
  }

  if (Object.keys(values).length === 0) next.delete(row.key);
  else
    next.set(row.key, {
      rowKey: row.key,
      sources: row.sources,
      values,
      originalValues,
    });
  return next;
}

/**
 * One Handsontable cell change (`[row, prop, oldValue, newValue]`), narrowed to
 * what the caps rule reads and writes.
 */
export type GridCellChange = [
  row: unknown,
  prop: unknown,
  oldValue: unknown,
  newValue: unknown,
];

function eachEditedCellText(
  changes: Array<GridCellChange | null>,
  visit: (cell: {
    change: GridCellChange;
    index: number;
    prop: string;
    text: string;
  }) => void,
): void {
  changes.forEach((change, index) => {
    if (!change) return;
    const [, prop, oldValue, newValue] = change;
    if (typeof prop !== 'string' || prop.startsWith('__')) return;
    if (newValue === oldValue) return;
    const text = asCellText(newValue);
    if (text === null) return;
    visit({ change, index, prop, text });
  });
}

/**
 * Fold a batch of grid cell changes to upper case, in place — the AS/400 upload
 * files this grid previews are upper case only, so a change set's cells are too.
 * Runs before the change lands, so typing, pasting a range from Excel and
 * auto-filling all show in the grid exactly what will be saved.
 *
 * Left alone: synthetic (`__`-prefixed) metadata columns, which are never
 * staged; a cleared cell, which has no text to case; and a change that doesn't
 * alter the value, so merely opening and closing a lower-case ingest cell isn't
 * turned into an edit.
 */
export function upperCaseCellChanges(
  changes: Array<GridCellChange | null>,
): void {
  eachEditedCellText(changes, ({ change, text }) => {
    change[3] = text.toUpperCase();
  });
}

/** One cell change refused for characters the AS/400 can't carry. */
export interface NonAsciiCellRejection {
  /** The rejected cell's edit key, so the caller can name its column. */
  editKey: string;
  /** The distinct offending characters, to show the author what to replace. */
  characters: string[];
}

/**
 * Fold a batch of grid cell changes to the ASCII the AS/400 accepts, in place,
 * and drop the ones that can't get there — reporting each with the characters
 * that stopped it.
 *
 * Two behaviours, for two kinds of character. Anything with an ASCII spelling is
 * rewritten silently (`TÉLÉPHONE` → `TELEPHONE`, a Word apostrophe → `'`, a
 * spreadsheet cell's line break → a space): the grid shows the folded value, so
 * what the author sees is what will be saved, exactly as with upper-casing.
 * Anything without one (`°`, `±`, `€`, non-Latin text) rejects the cell instead —
 * folding it would mean deleting part of a description, and `90°C` is the
 * author's to respell as `90C` or `90 DEG C`.
 *
 * Runs after {@link upperCaseCellChanges} (upper-casing `é` still leaves an
 * accent to fold). Character limits are checked on what lands, after both —
 * folding can lengthen a value (`Æ` → `AE`) — by the grid's cell rules (see
 * as400CellRules).
 */
export function foldCellChangesToAscii(
  changes: Array<GridCellChange | null>,
): NonAsciiCellRejection[] {
  const rejected: NonAsciiCellRejection[] = [];
  eachEditedCellText(changes, ({ change, index, prop, text }) => {
    const folded = foldToAs400Ascii(text);
    const characters = nonAs400AsciiCharacters(folded);
    if (characters.length > 0) {
      changes[index] = null;
      rejected.push({ editKey: prop, characters });
      return;
    }
    if (folded !== text) change[3] = folded;
  });
  return rejected;
}

export function normalizePriceCellChanges(
  changes: Array<GridCellChange | null>,
): void {
  eachEditedCellText(changes, ({ change, prop, text }) => {
    if (!isPriceNumberFieldCode(prop)) return;
    const next = applyPriceColumnNumber(text);
    if (next !== text) change[3] = next;
  });
}

export function rewriteGridCellChanges(
  changes: Array<GridCellChange | null>,
): NonAsciiCellRejection[] {
  upperCaseCellChanges(changes);
  normalizePriceCellChanges(changes);
  return foldCellChangesToAscii(changes);
}

export function correctSlashCountCellChanges(
  changes: Array<GridCellChange | null>,
  blankMarkerFor: (prop: string) => string | undefined,
): void {
  eachEditedCellText(changes, ({ change, prop, text }) => {
    const marker = blankMarkerFor(prop);
    if (!marker) return;
    const next = correctedSlashCount(text, marker);
    if (next !== null) change[3] = next;
  });
}

// Cell tints for the grid (inline styles — Handsontable renders outside
// Tailwind's class pipeline). In the After view: amber = unsaved edit, green =
// staged (saved) edit. In the Before view, edited cells tint red — the shown
// (old) value is being replaced. In both, blue = not edited, but carried into
// the change file because its paired field (or, for a Ship Direct flag, its
// supplier section) changed (see carriedWithChange).
// Opaque on purpose: Handsontable's text editor copies the cell's inline
// background onto the editor textarea, and a translucent tint shows the old value through.
const PENDING_CELL_TINT = 'rgb(253, 231, 194)';
const STAGED_CELL_TINT = 'rgb(220, 246, 229)';
const BEFORE_CELL_TINT = 'rgb(253, 233, 233)';
const CARRIED_CELL_TINT = 'rgb(224, 236, 252)';

/** A cell's baked edit state: staged (After view) or replaced (Before view). */
export type CellTintBase = 'staged' | 'changed';

/**
 * Background tint for one grid cell. A cell whose current value differs from
 * its persisted value is an unsaved (pending) edit; otherwise the baked base
 * state decides: 'staged' = a saved staged edit (After view), 'changed' = the
 * shown old value is being replaced (Before view). A cell with none of those
 * whose paired field changed is `carried`: the change file sends it anyway.
 */
export function cellTint(
  currentText: string | null,
  persistedText: string | null,
  base: CellTintBase | undefined,
  carried = false,
): string {
  if (currentText !== persistedText) return PENDING_CELL_TINT;
  if (base === 'staged') return STAGED_CELL_TINT;
  if (base === 'changed') return BEFORE_CELL_TINT;
  if (carried) return CARRIED_CELL_TINT;
  return '';
}

/**
 * Whether the change file carries `code` on this record though it is itself
 * unedited (`withAs400CarriedColumns`): the other half of a paired field is
 * changed, or — for a supplier section's Ship Direct flag — any of the
 * section's data is. The grid tints such a cell so a reviewer sees everything
 * the file will send. "Changed" means what the export means: a field's
 * effective value differs from its live one — a staged value equal to live is
 * no change, and carries nothing. Read at render time, not baked: an unsaved
 * edit to a trigger has to light this cell up as it is typed.
 */
export function carriedWithChange(
  record: As400GridRecord,
  code: string,
): boolean {
  const triggers = AS400_EXPORT_CARRIED_WITH.get(code);
  if (!triggers) return false;
  const otherSide = record.__counterpart ?? record.__persisted;
  return triggers.some(
    (trigger) =>
      trigger in record.__persisted &&
      asCellText(record[trigger]) !== asCellText(otherSide[trigger]),
  );
}

/**
 * One Handsontable source record: the visible cell values keyed by field code,
 * plus hidden metadata (`__`-prefixed, never bound to a grid column) that lets
 * the cell renderer and change handler work from the record alone — no React
 * state is read during Handsontable's own render.
 */
export interface As400GridRecord {
  /** The export row's stable key (`As400ExportRow.key`). */
  __key: string;
  /** Persisted value per code — what a cell shows when it has no unsaved edit. */
  __persisted: Record<string, string | null>;
  /** Revert value per code — what a reset writes ({@link revertCellValue}). */
  __revert: Record<string, string | null>;
  /** Baked tint state per code (see {@link cellTint}). */
  __tintBase: Record<string, CellTintBase>;
  /**
   * The other view's value per code — live values in the After view, the
   * effective (pending, else staged, else live) values in the Before view —
   * so a cell can tell whether its row really changes it
   * ({@link carriedWithChange}).
   */
  __counterpart?: Record<string, string | null>;
  __itemAdds?: ItemAddLookup;
  __stockUoms?: StockUomLookup;
  /** Cell values keyed by field code (string | null). */
  [code: string]: unknown;
}

/** Narrow a Handsontable source-data value to our grid record shape. */
export function isGridRecord(value: unknown): value is As400GridRecord {
  return value !== null && typeof value === 'object' && '__key' in value;
}

/** The Before view's baked tint states: any staged or pending edit = replaced. */
function beforeTintBase(
  row: As400ExportRow,
  pendingRow: PendingRowEdit | undefined,
): Record<string, CellTintBase> {
  const base: Record<string, CellTintBase> = {};
  for (const code of Object.keys(row.stagedValues ?? {}))
    base[code] = 'changed';
  for (const code of Object.keys(pendingRow?.values ?? {}))
    base[code] = 'changed';
  return base;
}

/** The After view's baked tint states: saved staged edits. */
function afterTintBase(row: As400ExportRow): Record<string, CellTintBase> {
  const base: Record<string, CellTintBase> = {};
  for (const code of Object.keys(row.stagedValues ?? {})) base[code] = 'staged';
  return base;
}

/**
 * A record's visible cells alongside the baseline the renderer tints against
 * and the values a reset puts back.
 */
interface RecordCells {
  cells: Record<string, string | null>;
  persisted: Record<string, string | null>;
  revert: Record<string, string | null>;
}

/**
 * The Before view's cells: the live ingest values, read-only history. Blank ↔
 * null is normalized so an empty ingest cell isn't tinted as a pending edit
 * against the renderer's asCellText-normalized value. An export-only column
 * has no live value — its Before cell is always blank.
 */
function beforeCells(
  row: As400ExportRow,
  worksheetColumns: readonly As400ExportColumn[],
): RecordCells {
  const cells: Record<string, string | null> = {};
  const persisted: Record<string, string | null> = {};
  for (const column of worksheetColumns) {
    const editKey = as400ColumnEditKey(column);
    if (!editKey) continue;
    const live = asCellText(row.values[editKey]);
    cells[editKey] = live;
    persisted[editKey] = live;
  }
  return { cells, persisted, revert: persisted };
}

/** A column's After value: its unsaved pending edit, else what is persisted. */
function effectiveCellValue(
  code: string,
  persistedValue: string | null,
  pendingRow: PendingRowEdit | undefined,
): string | null {
  if (!pendingRow || !(code in pendingRow.values)) return persistedValue;
  return pendingRow.values[code] ?? null;
}

/**
 * The After view's cells: the effective value per column — pending edit, else
 * staged edit, else live.
 */
function afterCells(
  row: As400ExportRow,
  worksheetColumns: readonly As400ExportColumn[],
  pendingRow: PendingRowEdit | undefined,
): RecordCells {
  const cells: Record<string, string | null> = {};
  const persisted: Record<string, string | null> = {};
  const revert: Record<string, string | null> = {};
  for (const column of worksheetColumns) {
    const editKey = as400ColumnEditKey(column);
    if (!editKey) continue;
    const persistedValue = persistedCellValue(row, editKey);
    cells[editKey] = effectiveCellValue(editKey, persistedValue, pendingRow);
    persisted[editKey] = persistedValue;
    revert[editKey] = revertCellValue(row, editKey);
  }
  return { cells, persisted, revert };
}

/**
 * Build the grid's source records for one page. Before view: the live ingest
 * values. After view: the effective values — pending edit, else staged edit,
 * else live. Pure — exported for unit testing.
 *
 * The grid deliberately shows every cell filled, unlike the download, whose
 * change files blank the cells the review isn't changing: the grid is where a
 * reviewer edits in context, so it needs the surrounding values on screen.
 */
export function buildGridRecords(
  rows: As400ExportRow[],
  worksheetColumns: readonly As400ExportColumn[],
  view: StagedView,
  pending: PendingEditsByRowKey,
  itemAdds?: ItemAddLookup,
  stockUoms?: StockUomLookup,
): As400GridRecord[] {
  return rows.map((row) => {
    const pendingRow = pending.get(row.key);
    const before = beforeCells(row, worksheetColumns);
    const after = afterCells(row, worksheetColumns, pendingRow);
    const { cells, persisted, revert } = view === 'before' ? before : after;
    return {
      ...cells,
      __key: row.key,
      __persisted: persisted,
      __revert: revert,
      __counterpart: view === 'before' ? after.cells : before.cells,
      __tintBase:
        view === 'before'
          ? beforeTintBase(row, pendingRow)
          : afterTintBase(row),
      __itemAdds: itemAdds,
      __stockUoms: stockUoms,
    };
  });
}

export function gridRecordsNeedRebuild(
  current: {
    rows: unknown;
    view: unknown;
    dataVersion: number;
    lookups?: unknown;
  },
  next: {
    rows: unknown;
    view: unknown;
    dataVersion: number;
    lookups?: unknown;
  },
): boolean {
  return (
    current.rows !== next.rows ||
    current.view !== next.view ||
    current.dataVersion !== next.dataVersion ||
    current.lookups !== next.lookups
  );
}
